import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, Mail, Save } from 'lucide-react';
import { FormDialog } from '@/components/dialog/FormDialog';
import { Field } from '@/components/form/Field';
import { SelectField } from '@/components/form/SelectField';
import { TextField } from '@/components/form/TextField';
import { useSubmit } from '@/hooks/useSubmit';
import UserModel from '@/models/entities/UserModel';
import { APP_ROLE } from '@/models/references/references';
import { useIsCurrentUser } from '@/security/useIsCurrentUser';
import { resolveReference, useReferenceOptions } from '@/stores/useReferenceOptions';
import { PASSWORD_MIN_LENGTH } from '@/security/password';
import { ReferenceOptions } from '@/components/form/ReferenceOptions';

const schema = z.object({
  email: z.email('Email invalide'),
  firstName: z.string().trim().min(1, 'Prénom requis'),
  lastName: z.string().trim().min(1, 'Nom requis'),
  /** Référence du rôle (ADMIN / USER) ; son IRI est retrouvée dans la liste chargée depuis l'API */
  appRole: z.string().min(1, 'Rôle requis'),
  plainPassword: z
    .string()
    .refine(
      (value) => value === '' || value.length >= PASSWORD_MIN_LENGTH,
      `${PASSWORD_MIN_LENGTH} caractères minimum`,
    ),
});

type UserForm = z.input<typeof schema>;

interface UserFormDialogProps {
  /** null : création */
  user: UserModel | null;
  onClose: () => void;
  onSaved: (user: UserModel) => void;
}

/**
 * Création / modification d'un utilisateur par un admin (POST / PATCH /users). Pas de mot de passe à la création :
 * l'admin n'a pas à connaître celui d'un autre ; l'utilisateur se connecte via son entreprise (SSO) ou se le définit
 * (« Mot de passe oublié ? »). À la modification, un nouveau mot de passe peut être imposé, au cas où.
 * Monté seulement quand il est ouvert (et avec une key par utilisateur) : le formulaire repart de ses valeurs initiales.
 */
export function UserFormDialog({ user, onClose, onSaved }: UserFormDialogProps) {
  const { submit, submitting, error } = useSubmit();
  const isCreation = !user;
  const appRoles = useReferenceOptions('appRoles');
  const isCurrentUser = useIsCurrentUser();
  // Un admin ne retire pas son propre rôle Administrateur (refusé aussi par l'API) : le rôle reste celui d'origine
  const roleLocked = !!user && user.appRoleReference === APP_ROLE.ADMIN && isCurrentUser(user.id);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UserForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: user?.email ?? '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      // Création : Utilisateur par défaut
      appRole: user?.appRoleReference ?? APP_ROLE.USER,
      plainPassword: '',
    },
  });

  const onSubmit = (values: UserForm) =>
    submit(async () => {
      const { plainPassword, appRole, ...rest } = schema.parse(values);
      const attributes = {
        ...rest,
        appRole: (await resolveReference('appRoles', appRole)).urn,
        ...(plainPassword ? { plainPassword } : {}),
      };
      const { model } = await new UserModel({ id: user?.id }).save(attributes, { silentError: true });
      onSaved(model);
    });

  return (
    <FormDialog
      title={isCreation ? 'Nouvel utilisateur' : `Modifier ${user.fullName}`}
      description={
        isCreation
          ? "L'utilisateur est créé actif. L'email doit correspondre à celui de son IAM (Okta / Entra)."
          : "L'email doit rester celui de son IAM (Okta / Entra)."
      }
      submitLabel={isCreation ? 'Créer' : 'Enregistrer'}
      submitIcon={Save}
      submitting={submitting}
      error={error}
      onSubmit={handleSubmit(onSubmit)}
      onClose={onClose}
    >
      <TextField
        id="email"
        label="Email"
        required
        type="email"
        icon={Mail}
        error={errors.email?.message}
        {...register('email')}
      />
      <div className="grid grid-cols-2 gap-4">
        <TextField id="lastName" label="Nom" required error={errors.lastName?.message} {...register('lastName')} />
        <TextField
          id="firstName"
          label="Prénom"
          required
          error={errors.firstName?.message}
          {...register('firstName')}
        />
      </div>
      {/* Affiché une fois les rôles chargés : sinon le navigateur prendrait la première option et non la valeur par défaut */}
      {roleLocked ? (
        <SelectField
          id="appRole"
          label="Rôle"
          required
          disabled
          value={APP_ROLE.ADMIN}
          hint="Vous ne pouvez pas retirer votre propre rôle Administrateur."
        >
          <option value={APP_ROLE.ADMIN}>{user.appRoleLabel}</option>
        </SelectField>
      ) : appRoles.length ? (
        <SelectField id="appRole" label="Rôle" required error={errors.appRole?.message} {...register('appRole')}>
          <ReferenceOptions name="appRoles" />
        </SelectField>
      ) : (
        <Field id="appRole" label="Rôle" required>
          <p className="h-9 flex items-center text-sm text-slate-500">Chargement…</p>
        </Field>
      )}
      {isCreation ? (
        <p className="text-sm text-slate-500">
          Il se connecte avec son compte d'entreprise (SSO). Pour un mot de passe local, il le définit lui-même avec
          « Mot de passe oublié ? » sur la page de connexion.
        </p>
      ) : (
        <TextField
          id="plainPassword"
          label="Nouveau mot de passe"
          type="password"
          autoComplete="new-password"
          placeholder="Vide : mot de passe inchangé"
          icon={Lock}
          error={errors.plainPassword?.message}
          hint="Changer le mot de passe déconnecte l'utilisateur de toutes ses sessions."
          {...register('plainPassword')}
        />
      )}
    </FormDialog>
  );
}
